import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { USER_LOGIN_PAGE_KEY, USER_TOKEN_KEY } from '../../constants/localStorageConstants'
import { IsaacMerchantResVO, LotteryVO } from '../../typings/merchant'
import { currentLottery, merchantInfo } from '../../api/merchantService'
import { getDictByKey } from '../../api/dictionaryService'

interface LotteryDataState {
  loading: boolean
  currentMerchant?: IsaacMerchantResVO
  lotteryChances: number
  currentLotteryInfo?: LotteryVO
  pointsPerLotteryNum: number
  customerServiceUrl?: string
  error?: string
}

interface UseLotteryDataResult extends LotteryDataState {
  currentPoints?: string
  totalPoints?: number
  refreshData: () => Promise<void>
  clearError: () => void
}

export const useLotteryData = (): UseLotteryDataResult => {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<LotteryDataState>({
    loading: true,
    lotteryChances: 0,
    pointsPerLotteryNum: 0,
    customerServiceUrl: '',
  })

  const mountedRef = useRef(true)
  const isInitializedRef = useRef(false)

  const calculateLotteryChances = useCallback((currentPoints: number, pointsPerLottery: number): number => {
    if (pointsPerLottery <= 0) return 0
    return Math.floor(currentPoints / pointsPerLottery)
  }, [])

  const fetchData = useCallback(async (): Promise<void> => {
    if (!mountedRef.current) return

    setState((prev) => ({ ...prev, loading: true, error: undefined }))

    let merchantData: IsaacMerchantResVO | undefined
    let pointsPerLotteryNum = 0
    let lotteryData: LotteryVO | undefined
    let customerServiceUrl = ''
    let errorMessage: string | undefined

    try {
      const [merchantResponse, pointsPerLottery, serviceUrl] = await Promise.all([
        merchantInfo(),
        getDictByKey('key_merchant_point_lottery_amount'),
        getDictByKey('key_wechat_customer_service_url'),
      ])
      merchantData = merchantResponse
      pointsPerLotteryNum = parseInt(pointsPerLottery, 10) || 0
      customerServiceUrl = serviceUrl || ''
    } catch (error) {
      errorMessage = error.errorMessage || '获取用户信息失败'
    }

    try {
      lotteryData = await currentLottery()
    } catch (error) {
      errorMessage = error.errorMessage || '获取抽奖信息失败，请稍后再试或者联系客服'
    }

    if (!mountedRef.current) return

    const currentPoints = parseInt(String(merchantData?.currentPoints || 0), 10)
    const chances = calculateLotteryChances(currentPoints, pointsPerLotteryNum)

    setState({
      loading: false,
      currentMerchant: merchantData,
      currentLotteryInfo: lotteryData,
      lotteryChances: chances,
      pointsPerLotteryNum,
      customerServiceUrl,
      error: errorMessage,
    })
  }, [calculateLotteryChances])

  const refreshData = useCallback(async (): Promise<void> => {
    await fetchData()
  }, [fetchData])

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: undefined }))
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isInitializedRef.current) return

    localStorage.setItem(USER_LOGIN_PAGE_KEY, '/merchantLogin')

    const token = searchParams.get('token')
    if (token) {
      localStorage.setItem(USER_TOKEN_KEY, token)
    }

    isInitializedRef.current = true
    fetchData().then()
  }, [fetchData, searchParams])

  return {
    ...state,
    currentPoints: state.currentMerchant?.currentPoints,
    totalPoints: state.currentMerchant?.totalPoints,
    refreshData,
    clearError,
  }
}
